/**
 * Registro cassa giornaliero — quadratura, turni e riepilogo incassi.
 *
 * Mostra i turni del giorno selezionato con fondo cassa, incassi
 * per metodo di pagamento e quadratura.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';

/* ─────────────────────────── Utility ─────────────────────────── */

const authHeadersGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Restituisce YYYY-MM-DD dalla data locale. */
const toISODate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/* ─────────────────────────── Interfacce ─────────────────────────── */

interface TurnoInfo {
  id: string;
  stato: string;
  apertura_at: string;
  chiusura_at: string | null;
  fondo_cassa_iniziale: string;
  fondo_cassa_finale: string | null;
  totale_contanti: string;
  totale_elettronico: string;
  totale_assegni: string;
  totale_altro: string;
  scostamento: string | null;
}

/* ─────────────────────────── Componente ─────────────────────────── */

const RegistroCassa: React.FC = () => {
  const navigate = useNavigate();
  const fmt = useFormatters();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [turni, setTurni] = useState<TurnoInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Carica tutti i turni e filtra per data selezionata. */
  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/cassa/turni', { headers: authHeadersGet() });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: TurnoInfo[] = await res.json();
      const target = toISODate(selectedDate);
      const filtered = data.filter((t) => t.apertura_at.startsWith(target));
      setTurni(filtered);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento turni');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    void carica();
  }, [carica]);

  /** Naviga al giorno precedente/successivo. */
  const cambiaGiorno = (delta: number) => {
    setSelectedDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + delta);
      return next;
    });
  };

  const isToday = toISODate(selectedDate) === toISODate(new Date());

  /* ── Aggregazioni ── */
  const totContanti = turni.reduce((s, t) => s + Number(t.totale_contanti), 0);
  const totElettronico = turni.reduce((s, t) => s + Number(t.totale_elettronico), 0);
  const totAssegni = turni.reduce((s, t) => s + Number(t.totale_assegni), 0);
  const totAltro = turni.reduce((s, t) => s + Number(t.totale_altro), 0);
  const totIncasso = totContanti + totElettronico + totAssegni + totAltro;
  const turniChiusi = turni.filter((t) => t.stato === 'chiuso');
  const turniAperti = turni.filter((t) => t.stato === 'aperto');
  const scostamentoTotale = turniChiusi.reduce(
    (s, t) => s + Number(t.scostamento ?? 0),
    0,
  );

  return (
    <div className="space-y-6 p-6">
      {/* ── Header con selettore data ── */}
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Registro cassa
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => cambiaGiorno(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={toISODate(selectedDate)}
              onChange={(e) => setSelectedDate(new Date(e.target.value + 'T00:00:00'))}
              className="w-40"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => cambiaGiorno(1)}
            disabled={isToday}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isToday && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedDate(new Date())}
            >
              Oggi
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm capitalize text-muted-foreground">
        {fmt.extendedDate(selectedDate)}
      </p>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* ── Card riepilogo giornaliero ── */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Turni</p>
            <p className="font-mono text-2xl font-semibold">{turni.length}</p>
            <p className="text-xs text-muted-foreground">
              {turniChiusi.length} chiusi, {turniAperti.length} aperti
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Incasso totale</p>
            <p className="font-mono text-2xl font-semibold">
              {fmt.currency(totIncasso)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Contanti</p>
            <p className="font-mono text-2xl font-semibold">
              {fmt.currency(totContanti)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Quadratura</p>
            <p
              className={`font-mono text-2xl font-semibold ${
                scostamentoTotale !== 0 ? 'text-destructive' : ''
              }`}
            >
              {fmt.currency(scostamentoTotale)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Riepilogo per metodo di pagamento ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Incassi per metodo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Contanti</span>
              <span className="font-mono">{fmt.currency(totContanti)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Elettronico</span>
              <span className="font-mono">{fmt.currency(totElettronico)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Assegni</span>
              <span className="font-mono">{fmt.currency(totAssegni)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Altro</span>
              <span className="font-mono">{fmt.currency(totAltro)}</span>
            </div>
          </div>
          <Separator className="my-3" />
          <div className="flex justify-between text-sm font-semibold">
            <span>Totale giornata</span>
            <span className="font-mono">{fmt.currency(totIncasso)}</span>
          </div>
        </CardContent>
      </Card>

      {/* ── Tabella turni ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Turni del giorno</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">Caricamento…</p>
          )}
          {!loading && turni.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Nessun turno registrato per questa data
            </p>
          )}
          {!loading && turni.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Apertura</TableHead>
                  <TableHead>Chiusura</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Fondo iniz.</TableHead>
                  <TableHead className="text-right">Fondo fin.</TableHead>
                  <TableHead className="text-right">Contanti</TableHead>
                  <TableHead className="text-right">Elettronico</TableHead>
                  <TableHead className="text-right">Scostamento</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {turni.map((t: TurnoInfo) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono text-sm">
                      {fmt.time(t.apertura_at)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {t.chiusura_at ? fmt.time(t.chiusura_at) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.stato === 'aperto' ? 'outline' : 'secondary'}>
                        {t.stato}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt.currency(t.fondo_cassa_iniziale)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {t.fondo_cassa_finale ? fmt.currency(t.fondo_cassa_finale) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt.currency(t.totale_contanti)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt.currency(t.totale_elettronico)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {t.scostamento != null ? (
                        <span
                          className={Number(t.scostamento) !== 0 ? 'text-destructive' : ''}
                        >
                          {fmt.currency(t.scostamento)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/cassa/turni/${t.id}`)}
                      >
                        Dettaglio
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistroCassa;
